import { useEffect, useState } from 'react';
import { studentsApi, type Student } from '../api';
import { useStudent } from '../context/StudentContext';
import { Plus, Edit2, Trash2, Star, BookOpen, User } from 'lucide-react';

type EducationLevel = 'senior' | 'junior';

const seniorGrades = ['高一', '高二', '高三'];
const juniorGrades = ['初一', '初二', '初三'];
const seniorSubjects = ['物理类', '历史类'];
const seniorTargets = ['985', '985/211', '211', '一本'];
const juniorTargets = ['一双外', '普高', '职高', '示范高中'];

function getDefaultForm(eduLevel: EducationLevel) {
  return {
    name: '',
    school: '',
    grade: eduLevel === 'senior' ? '高二' : '初二',
    subject_type: '物理类',
    grade_total: eduLevel === 'senior' ? 750 : 800,
    target_level: eduLevel === 'senior' ? '985/211' : '普高',
    education_level: eduLevel,
    notes: '',
  };
}

export default function Students() {
  const { students, refreshStudents, currentStudent, setCurrentStudent } = useStudent();
  const [showModal, setShowModal] = useState(false);
  const [editStudent, setEditStudent] = useState<Student | null>(null);
  const [form, setForm] = useState(getDefaultForm('senior'));
  const [saving, setSaving] = useState(false);

  const openAdd = () => {
    setEditStudent(null);
    setForm(getDefaultForm('senior'));
    setShowModal(true);
  };

  const openEdit = (s: Student) => {
    setEditStudent(s);
    setForm({
      name: s.name,
      school: s.school,
      grade: s.grade,
      subject_type: s.subject_type,
      grade_total: s.grade_total,
      target_level: s.target_level,
      education_level: (s as any).education_level || 'senior',
      notes: s.notes,
    });
    setShowModal(true);
  };

  const handleEducationLevelChange = (eduLevel: EducationLevel) => {
    setForm({
      ...form,
      education_level: eduLevel,
      grade: eduLevel === 'senior' ? '高一' : '初一',
      subject_type: '物理类',
      grade_total: eduLevel === 'senior' ? 750 : 800,
      target_level: eduLevel === 'senior' ? '985/211' : '普高',
    });
  };

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    if (editStudent) {
      await studentsApi.update(editStudent.id, form);
    } else {
      await studentsApi.create(form);
    }
    await refreshStudents();
    setSaving(false);
    setShowModal(false);
  };

  const handleDelete = async (s: Student) => {
    if (s.is_default) return;
    if (!confirm(`确定删除学生 ${s.name}？`)) return;
    await studentsApi.delete(s.id);
    await refreshStudents();
  };

  const eduLevel = form.education_level || 'senior';
  const gradeOptions = eduLevel === 'senior' ? seniorGrades : juniorGrades;
  const targetOptions = eduLevel === 'senior' ? seniorTargets : juniorTargets;
  const isJunior = eduLevel === 'junior';

  return (
    <div className="p-3 md:p-6 max-w-5xl mx-auto">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4 md:mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">学生管理</h1>
          <p className="text-gray-500 text-sm mt-1">管理系统中的学生档案，支持多学生成绩追踪</p>
        </div>
        <button onClick={openAdd}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors shadow-sm">
          <Plus size={16} /> 添加学生
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {students.map(s => (
          <StudentCard
            key={s.id}
            student={s}
            isActive={currentStudent?.id === s.id}
            onSelect={() => setCurrentStudent(s)}
            onEdit={() => openEdit(s)}
            onDelete={() => handleDelete(s)}
          />
        ))}
        <button onClick={openAdd}
          className="h-48 rounded-2xl border-2 border-dashed border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-all flex flex-col items-center justify-center text-gray-400 hover:text-blue-500">
          <Plus size={28} />
          <span className="text-sm mt-2">添加学生</span>
        </button>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold mb-4">{editStudent ? '编辑学生' : '添加学生'}</h3>
            <div className="space-y-3">
              <FormField label="姓名 *">
                <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                  className="w-full border rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" placeholder="学生姓名" />
              </FormField>
              <FormField label="学校">
                <input value={form.school} onChange={e => setForm({ ...form, school: e.target.value })}
                  className="w-full border rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" placeholder="例：福建省厦门一中" />
              </FormField>

              {/* 学段选择器 */}
              <FormField label="学段">
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => handleEducationLevelChange('senior')}
                    className={`flex-1 py-2 rounded-xl text-sm font-medium transition-colors ${
                      eduLevel === 'senior'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    高中
                  </button>
                  <button
                    type="button"
                    onClick={() => handleEducationLevelChange('junior')}
                    className={`flex-1 py-2 rounded-xl text-sm font-medium transition-colors ${
                      eduLevel === 'junior'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    初中
                  </button>
                </div>
              </FormField>

              <div className="grid grid-cols-2 gap-3">
                <FormField label="年级">
                  <select value={form.grade} onChange={e => setForm({ ...form, grade: e.target.value })}
                    className="w-full border rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300">
                    {gradeOptions.map(g => <option key={g}>{g}</option>)}
                  </select>
                </FormField>
                <FormField label="科目组">
                  <select
                    value={form.subject_type}
                    onChange={e => setForm({ ...form, subject_type: e.target.value })}
                    disabled={isJunior}
                    className={`w-full border rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 ${
                      isJunior ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : ''
                    }`}
                  >
                    {seniorSubjects.map(s => <option key={s}>{s}</option>)}
                  </select>
                </FormField>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <FormField label="满分总分">
                  <input type="number" value={form.grade_total} onChange={e => setForm({ ...form, grade_total: parseInt(e.target.value) || 0 })}
                    className="w-full border rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" />
                </FormField>
                <FormField label="目标层次">
                  <select value={form.target_level} onChange={e => setForm({ ...form, target_level: e.target.value })}
                    className="w-full border rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300">
                    {targetOptions.map(t => <option key={t}>{t}</option>)}
                  </select>
                </FormField>
              </div>
              <FormField label="备注">
                <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
                  className="w-full border rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 h-16 resize-none" placeholder="其他信息" />
              </FormField>
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={() => setShowModal(false)}
                className="flex-1 py-2 rounded-xl border hover:bg-gray-50 text-sm text-gray-600">取消</button>
              <button onClick={handleSave} disabled={saving}
                className="flex-1 py-2 rounded-xl bg-blue-600 text-white text-sm hover:bg-blue-700 disabled:opacity-50">
                {saving ? '保存中...' : '保存'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StudentCard({ student: s, isActive, onSelect, onEdit, onDelete }: {
  student: Student; isActive: boolean; onSelect: () => void; onEdit: () => void; onDelete: () => void;
}) {
  const eduLevel = (s as any).education_level || 'senior';
  const eduLabel = eduLevel === 'junior' ? '初中' : '高中';
  return (
    <div className={`bg-white rounded-2xl p-5 border-2 transition-all cursor-pointer ${isActive ? 'border-blue-400 shadow-md shadow-blue-100' : 'border-gray-100 hover:border-blue-200 hover:shadow-sm'}`}
      onClick={onSelect}>
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-400 to-indigo-600 flex items-center justify-center text-lg font-bold text-white">
            {s.name[0]}
          </div>
          <div>
            <div className="flex items-center gap-1.5">
              <span className="font-semibold text-gray-800">{s.name}</span>
              <span className="text-xs bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded font-medium">{eduLabel}</span>
              {s.is_default === 1 && (
                <span className="text-xs bg-green-100 text-green-600 px-1.5 py-0.5 rounded font-medium">默认</span>
              )}
            </div>
            <div className="text-xs text-gray-500 mt-0.5">{s.grade} · {s.subject_type}</div>
          </div>
        </div>
        <div className="flex gap-1" onClick={e => e.stopPropagation()}>
          <button onClick={onEdit} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors text-gray-400 hover:text-blue-500">
            <Edit2 size={14} />
          </button>
          {!s.is_default && (
            <button onClick={onDelete} className="p-1.5 hover:bg-red-50 rounded-lg transition-colors text-gray-400 hover:text-red-500">
              <Trash2 size={14} />
            </button>
          )}
        </div>
      </div>

      <div className="mt-4 space-y-1.5 text-sm">
        <InfoRow icon={<User size={13} />} label={s.school || '未设置学校'} />
        <InfoRow icon={<Star size={13} />} label={`目标：${s.target_level}`} />
        <InfoRow icon={<BookOpen size={13} />} label={`满分 ${s.grade_total} 分`} />
      </div>

      {s.latest_score && (
        <div className="mt-3 bg-gray-50 rounded-xl p-2.5 text-xs text-gray-600">
          最近：{s.latest_score.exam_name} {s.latest_score.total_score}分 · 年级第{s.latest_score.grade_rank}名
        </div>
      )}
      {!s.latest_score && (
        <div className="mt-3 bg-gray-50 rounded-xl p-2.5 text-xs text-gray-400">暂无成绩记录</div>
      )}
    </div>
  );
}

function FormField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      {children}
    </div>
  );
}

function InfoRow({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <div className="flex items-center gap-2 text-gray-600">
      <span className="text-gray-400">{icon}</span>
      <span className="truncate">{label}</span>
    </div>
  );
}
